// ============================================================
// FILE: App.js
// LOCATION: C:\gardenmarket\web\src\App.js
// ============================================================
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import Navbar from './components/Navbar';
import VersionFooter from './components/VersionFooter';

import './responsive.css';   // mobile grid collapsing + 16px input floor

import Home               from './pages/public/Home';
import Login              from './pages/public/Login';
import Register           from './pages/public/Register';
import ForgotPassword     from './pages/public/ForgotPassword';
import GardenerLink       from './pages/public/GardenerLink';
import ResetPassword      from './pages/public/ResetPassword';

import BuyerDashboard         from './pages/buyer/BuyerDashboard';
import PostJob                from './pages/buyer/PostJob';
import JobDetail              from './pages/buyer/JobDetail';
import EditJob                from './pages/buyer/EditJob';
import BrowseGardeners        from './pages/buyer/BrowseGardeners';
import PublicGardenerProfile  from './pages/buyer/PublicGardenerProfile';
import LeaveReview            from './pages/buyer/LeaveReview';

import GardenerDashboard  from './pages/gardener/GardenerDashboard';
import GardenerProfile    from './pages/gardener/GardenerProfile';
import SubmitQuote        from './pages/gardener/SubmitQuote';
import MyBookings         from './pages/gardener/MyBookings';
import BookingDetail      from './pages/gardener/BookingDetail';
import PublicBuyerProfile from './pages/gardener/PublicBuyerProfile';
import CreditsPage        from './pages/gardener/CreditsPage';

import AdminDashboard from './pages/admin/AdminDashboard';

function ProtectedRoute({ children, allowedTypes }) {
    const { user } = useAuth();
    if (!user) return <Navigate to="/login" />;
    if (allowedTypes && !allowedTypes.includes(user.user_type)) return <Navigate to="/" />;
    return children;
}

function HomeRoute() {
    const { user } = useAuth();
    if (user?.user_type === 'gardener') return <Navigate to="/gardener/dashboard" />;
    if (user?.user_type === 'buyer')    return <Navigate to="/buyer/dashboard" />;
    if (user?.user_type === 'admin')    return <Navigate to="/admin/dashboard" />;
    return <Home />;
}

function AppRoutes() {
    return (
        <div style={{ minHeight:'100vh', backgroundColor:'#f8fdf9' }}>
            <Navbar />
            <Routes>
                <Route path="/"         element={<HomeRoute />} />
                <Route path="/login"    element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password"  element={<ResetPassword />} />
                {/* Gardener's shareable link — /g/MIKE4K2P.
                    Public: the whole point is that a gardener can hand
                    it to a customer who has no account yet. */}
                <Route path="/g/:code" element={<GardenerLink />} />

                {/* ── Buyer routes ── */}
                <Route path="/buyer/dashboard"      element={<ProtectedRoute allowedTypes={['buyer']}><BuyerDashboard /></ProtectedRoute>} />
                <Route path="/buyer/post-job"        element={<ProtectedRoute allowedTypes={['buyer']}><PostJob /></ProtectedRoute>} />
                <Route path="/buyer/job/:id"         element={<ProtectedRoute allowedTypes={['buyer']}><JobDetail /></ProtectedRoute>} />
                <Route path="/buyer/job/:id/edit"    element={<ProtectedRoute allowedTypes={['buyer']}><EditJob /></ProtectedRoute>} />
                <Route path="/buyer/job/:id/review"  element={<ProtectedRoute allowedTypes={['buyer','gardener']}><LeaveReview /></ProtectedRoute>} />
                <Route path="/gardeners"             element={<ProtectedRoute allowedTypes={['buyer']}><BrowseGardeners /></ProtectedRoute>} />
                <Route path="/gardener-profile/:id"  element={<ProtectedRoute allowedTypes={['buyer']}><PublicGardenerProfile /></ProtectedRoute>} />

                {/* ── Gardener routes ── */}
                <Route path="/gardener/dashboard"    element={<ProtectedRoute allowedTypes={['gardener']}><GardenerDashboard /></ProtectedRoute>} />
                <Route path="/gardener/profile"      element={<ProtectedRoute allowedTypes={['gardener']}><GardenerProfile /></ProtectedRoute>} />
                <Route path="/gardener/bookings"     element={<ProtectedRoute allowedTypes={['gardener']}><MyBookings /></ProtectedRoute>} />
                <Route path="/gardener/booking/:id"  element={<ProtectedRoute allowedTypes={['gardener']}><BookingDetail /></ProtectedRoute>} />
                <Route path="/gardener/job/:id/quote"  element={<ProtectedRoute allowedTypes={['gardener']}><SubmitQuote /></ProtectedRoute>} />
                <Route path="/gardener/job/:id/review" element={<ProtectedRoute allowedTypes={['buyer','gardener']}><LeaveReview /></ProtectedRoute>} />

                {/* NEW: Public buyer profile — gardeners click buyer name on job feed */}
                <Route path="/gardener/credits"     element={<ProtectedRoute allowedTypes={['gardener']}><CreditsPage /></ProtectedRoute>} />
                <Route path="/buyer-profile/:id"  element={<ProtectedRoute allowedTypes={['gardener']}><PublicBuyerProfile /></ProtectedRoute>} />

                {/* ── Admin routes ── */}
                <Route path="/admin/dashboard" element={<ProtectedRoute allowedTypes={['admin']}><AdminDashboard /></ProtectedRoute>} />

                <Route path="*" element={<Navigate to="/" />} />
            </Routes>
            <VersionFooter />
        </div>
    );
}

export default function App() {
    return (
        <AuthProvider>
            <BrowserRouter>
                <AppRoutes />
            </BrowserRouter>
        </AuthProvider>
    );
}
